import { useMemo, useState } from 'react';
import { ShieldCheck, Users } from 'lucide-react';
import { MasterTopbar } from '../shell/MasterTopbar';
import { MasterAuditRow } from '../components/MasterAuditRow';
import { useMasterAudit, useMasterWorkspaces } from '../hooks/useMasterData';
import { describeAuditAction, type AuditType } from '../lib/audit-format';

type Filter = 'todos' | AuditType;

const FILTERS: ReadonlyArray<[Filter, string]> = [
  ['todos', 'Tudo'],
  ['prest', 'Prestação'],
  ['billing', 'Cobrança'],
  ['sec', 'Segurança'],
  ['user', 'Usuários'],
  ['ws', 'Workspaces'],
];

function isWithin24h(iso: string, now = Date.now()): boolean {
  return now - new Date(iso).getTime() < 24 * 60 * 60 * 1000;
}

export function MasterAuditoriaScreen() {
  const { data, isLoading, error } = useMasterAudit();
  const { data: wsData } = useMasterWorkspaces();
  const [filter, setFilter] = useState<Filter>('todos');

  const items = data?.items ?? [];

  const wsNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const w of wsData?.items ?? []) map.set(w.id, w.name);
    return map;
  }, [wsData]);

  const filtered = useMemo(() => {
    return items.filter((a) => {
      if (filter === 'todos') return true;
      const d = describeAuditAction(a.action, a.metadata);
      return d.type === filter;
    });
  }, [items, filter]);

  const events24h = items.filter((a) => isWithin24h(a.createdAt)).length;
  const logins = items.filter((a) => a.action === 'user.login' || a.action.startsWith('auth.login')).length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '0 18px 28px' }}>
      <MasterTopbar title="Auditoria" sub="Registro de eventos" />

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <KpiSmall
          icon={<ShieldCheck size={15} />}
          label="Eventos · 24h"
          value={String(events24h)}
          hint="janela móvel"
          tone="ink"
        />
        <KpiSmall icon={<Users size={15} />} label="Logins" value={String(logins)} hint="histórico recente" />
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 6, overflow: 'auto', paddingBottom: 2 }}>
        {FILTERS.map(([id, label]) => {
          const active = filter === id;
          return (
            <button
              key={id}
              onClick={() => setFilter(id)}
              style={{
                flex: '0 0 auto',
                padding: '7px 13px',
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 600,
                whiteSpace: 'nowrap',
                background: active ? 'var(--lilac-deep)' : 'var(--card-soft)',
                color: active ? '#fff' : 'var(--fg-muted)',
                border: active ? '1px solid transparent' : '1px solid var(--card-stroke)',
                cursor: 'pointer',
                font: 'inherit',
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* Lista */}
      <div
        style={{
          padding: 14,
          background: 'var(--card)',
          border: '1px solid var(--card-stroke)',
          borderRadius: 18,
        }}
      >
        <div
          style={{
            fontSize: 10.5,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: 'var(--fg-muted)',
            fontFamily: 'var(--font-mono)',
            marginBottom: 12,
          }}
        >
          Eventos
        </div>

        {isLoading && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} style={{ height: 36, background: 'var(--card-soft)', borderRadius: 10 }} />
            ))}
          </div>
        )}

        {error && (
          <div style={{ color: 'var(--block)', fontSize: 13 }}>{(error as Error).message}</div>
        )}

        {!isLoading && filtered.length === 0 && !error && (
          <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--fg-dim)', fontSize: 12.5 }}>
            Nenhum evento neste filtro.
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {filtered.map((a) => (
            <MasterAuditRow
              key={a.id}
              action={a.action}
              workspaceName={a.workspaceId ? wsNameById.get(a.workspaceId) ?? null : null}
              createdAt={a.createdAt}
              metadata={a.metadata}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

interface KpiSmallProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
  tone?: 'default' | 'ink';
}
function KpiSmall({ icon, label, value, hint, tone = 'default' }: KpiSmallProps) {
  const isInk = tone === 'ink';
  return (
    <div
      style={{
        padding: 12,
        background: isInk ? 'var(--ink-deep)' : 'var(--card)',
        border: isInk ? '1px solid rgba(255,255,255,0.05)' : '1px solid var(--card-stroke)',
        borderRadius: 14,
        color: isInk ? 'var(--on-ink)' : 'var(--fg)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span
          style={{
            fontSize: 9.5,
            textTransform: 'uppercase',
            letterSpacing: '0.07em',
            color: isInk ? 'var(--on-ink-muted)' : 'var(--fg-muted)',
            fontFamily: 'var(--font-mono)',
            fontWeight: 600,
          }}
        >
          {label}
        </span>
        <span style={{ color: isInk ? 'var(--on-ink-muted)' : 'var(--fg-muted)' }}>{icon}</span>
      </div>
      <div
        style={{
          fontSize: 22,
          fontWeight: 700,
          letterSpacing: '-0.02em',
          marginTop: 6,
          fontFamily: 'var(--font-mono)',
          lineHeight: 1,
        }}
      >
        {value}
      </div>
      {hint && (
        <div
          style={{
            fontSize: 10,
            fontFamily: 'var(--font-mono)',
            color: isInk ? 'var(--on-ink-muted)' : 'var(--fg-muted)',
            marginTop: 4,
          }}
        >
          {hint}
        </div>
      )}
    </div>
  );
}
